import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Skeleton } from '@/components/ui/skeleton';
import {
  FileText, Upload, Download, Trash2, Search, FolderOpen, Plus, User,
  File, Building2, ExternalLink, Ban, RotateCcw, AlertTriangle,
} from 'lucide-react';
import { toast } from 'sonner';
import { isAdmin, isHR, getUser } from '@/lib/auth';
import api from '@/lib/api';

// ─── Helpers ──────────────────────────────────────────────
function canManageDocs() {
  return isAdmin() || isHR();
}

const FILE_ICON_COLOR = {
  'application/pdf': 'text-red-500',
  'image/jpeg': 'text-blue-500',
  'image/png': 'text-blue-500',
  'image/webp': 'text-blue-500',
};

function formatSize(bytes) {
  if (!bytes) return '--';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

const PERSONAL_CATEGORIES = [
  'Resume / CV', 'ID Proof', 'Address Proof', 'Certificates',
  'Bank Statements', 'Tax Documents', 'Insurance', 'Other',
];

// Open document in new tab by fetching blob with auth & using object URL
async function openInNewTab(doc) {
  try {
    const r = await api.get(`/api/documents/${doc.id}/download`, { responseType: 'blob' });
    const blob = new Blob([r.data], { type: doc.content_type || 'application/octet-stream' });
    const url = window.URL.createObjectURL(blob);
    window.open(url, '_blank', 'noopener,noreferrer');
    // Revoke after a delay so the new tab has time to load
    setTimeout(() => window.URL.revokeObjectURL(url), 60_000);
  } catch (e) {
    toast.error('Failed to open document');
  }
}

async function downloadDoc(doc) {
  try {
    const r = await api.get(`/api/documents/${doc.id}/download`, { responseType: 'blob' });
    const url = window.URL.createObjectURL(new Blob([r.data]));
    const link = document.createElement('a');
    link.href = url;
    link.download = doc.filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  } catch (e) {
    toast.error('Download failed');
  }
}

// ─── Doc Row ──────────────────────────────────────────────
function DocRow({ doc, canManage, canDelete, onReject, onUndoReject, onDelete, onRefresh }) {
  const isRejected = doc.status === 'rejected';
  return (
    <div
      className={`group rounded-lg border transition-colors ${
        isRejected ? 'border-red-200 bg-red-50/40' : 'border-slate-200 bg-white hover:border-[#0D2850]/30'
      }`}
      data-testid={`doc-${doc.id}`}
    >
      <div className="flex items-center gap-3 p-3">
        <File className={`w-5 h-5 flex-shrink-0 ${FILE_ICON_COLOR[doc.content_type] || 'text-slate-400'}`} />
        <div className="flex-1 min-w-0">
          <button
            type="button"
            onClick={() => openInNewTab(doc)}
            className="text-sm font-medium text-[#0D2850] hover:underline text-left truncate block max-w-full"
            title={`Open ${doc.filename} in new tab`}
            data-testid={`doc-link-${doc.id}`}
          >
            {doc.filename}
          </button>
          <p className="text-[11px] text-slate-400 truncate">
            {formatSize(doc.file_size)} · {doc.uploaded_at ? new Date(doc.uploaded_at).toLocaleDateString('en-IN') : '--'}
            {doc.uploaded_by ? ` · by ${doc.uploaded_by}` : ''}
          </p>
          {doc.notes && <p className="text-[11px] text-slate-400 italic truncate">{doc.notes}</p>}
        </div>
        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          <Button
            variant="ghost" size="sm" className="h-7 w-7 p-0"
            onClick={() => openInNewTab(doc)} title="Open in new tab"
            data-testid={`doc-view-${doc.id}`}
          >
            <ExternalLink className="w-3.5 h-3.5 text-[#0D2850]" />
          </Button>
          <Button
            variant="ghost" size="sm" className="h-7 w-7 p-0"
            onClick={() => downloadDoc(doc)} title="Download"
            data-testid={`doc-download-${doc.id}`}
          >
            <Download className="w-3.5 h-3.5 text-[#0D2850]" />
          </Button>
          {canManage && !isRejected && (
            <Button
              variant="ghost" size="sm" className="h-7 w-7 p-0"
              onClick={() => onReject?.(doc)} title="Reject document"
              data-testid={`doc-reject-${doc.id}`}
            >
              <Ban className="w-3.5 h-3.5 text-amber-600" />
            </Button>
          )}
          {canManage && isRejected && (
            <Button
              variant="ghost" size="sm" className="h-7 w-7 p-0"
              onClick={() => onUndoReject?.(doc)} title="Undo rejection"
              data-testid={`doc-undo-reject-${doc.id}`}
            >
              <RotateCcw className="w-3.5 h-3.5 text-violet-600" />
            </Button>
          )}
          {canDelete && (
            <Button
              variant="ghost" size="sm" className="h-7 w-7 p-0"
              onClick={() => onDelete?.(doc.id)} title="Delete permanently"
              data-testid={`doc-delete-${doc.id}`}
            >
              <Trash2 className="w-3.5 h-3.5 text-red-500" />
            </Button>
          )}
        </div>
      </div>
      {isRejected && (
        <div className="flex items-start gap-2 border-t border-red-200 bg-red-50 px-3 py-2 rounded-b-lg">
          <AlertTriangle className="w-3.5 h-3.5 text-red-500 flex-shrink-0 mt-0.5" />
          <div className="min-w-0 flex-1">
            <p className="text-[11px] font-semibold text-red-700">
              REJECTED{doc.rejected_by ? ` by ${doc.rejected_by}` : ''}
              {doc.rejected_at ? ` · ${new Date(doc.rejected_at).toLocaleDateString('en-IN')}` : ''}
            </p>
            <p className="text-[11px] text-red-600 whitespace-normal break-words" data-testid={`doc-rejection-reason-${doc.id}`}>
              {doc.rejection_reason || 'No reason provided'}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Folder Card ──────────────────────────────────────────
function FolderCard({ empGroup, onOpen, canManage, onUpload }) {
  const docCount = Object.values(empGroup.categories || {}).reduce((s, arr) => s + arr.length, 0);
  const catCount = Object.keys(empGroup.categories || {}).length;
  return (
    <button
      type="button"
      onClick={() => onOpen(empGroup)}
      className="group relative text-left rounded-xl border border-slate-200 bg-white hover:border-[#0D2850]/40 hover:shadow-md hover:-translate-y-0.5 transition-all p-4 flex flex-col gap-3"
      data-testid={`folder-${empGroup.employee_code || empGroup.employee_id}`}
    >
      <div className="flex items-start justify-between">
        <div className="w-11 h-11 rounded-lg bg-gradient-to-br from-amber-100 to-amber-200 flex items-center justify-center">
          <FolderOpen className="w-6 h-6 text-amber-600" />
        </div>
        {canManage && (
          <span
            role="button"
            tabIndex={0}
            onClick={(e) => { e.stopPropagation(); onUpload?.(empGroup.employee_id); }}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.stopPropagation(); onUpload?.(empGroup.employee_id); } }}
            className="opacity-0 group-hover:opacity-100 transition-opacity h-7 w-7 rounded-md hover:bg-slate-100 flex items-center justify-center cursor-pointer"
            title="Upload document to this folder"
            data-testid={`folder-upload-${empGroup.employee_code || empGroup.employee_id}`}
          >
            <Upload className="w-3.5 h-3.5 text-[#0D2850]" />
          </span>
        )}
      </div>
      <div>
        <p className="text-sm font-semibold text-[#0B1B33] truncate" title={empGroup.employee_name}>
          {empGroup.employee_name || 'Unknown'}
        </p>
        {empGroup.employee_code && (
          <p className="text-xs text-slate-400 font-mono">{empGroup.employee_code}</p>
        )}
      </div>
      <div className="flex items-center gap-2">
        <Badge className="bg-slate-100 text-slate-600 hover:bg-slate-100 text-[10px]">{docCount} docs</Badge>
        {catCount > 0 && (
          <Badge className="bg-blue-50 text-blue-700 hover:bg-blue-50 text-[10px]">{catCount} {catCount === 1 ? 'category' : 'categories'}</Badge>
        )}
      </div>
    </button>
  );
}

// ─── Employee Docs Dialog ────────────────────────────────
function EmployeeDocsDialog({ empGroup, open, onOpenChange, canManage, canDeleteAll, onRefresh, onUpload, onReject, onUndoReject, onDelete }) {
  if (!empGroup) return null;
  const catEntries = Object.entries(empGroup.categories || {});
  const docCount = catEntries.reduce((s, [, docs]) => s + docs.length, 0);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="employee-docs-dialog">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-amber-100 to-amber-200 flex items-center justify-center">
              <FolderOpen className="w-5 h-5 text-amber-600" />
            </div>
            <div className="flex-1 min-w-0">
              <DialogTitle className="text-[#0B1B33] truncate">{empGroup.employee_name}</DialogTitle>
              <p className="text-xs text-slate-400">
                {empGroup.employee_code ? `${empGroup.employee_code} · ` : ''}
                {docCount} document{docCount === 1 ? '' : 's'} · {catEntries.length} categor{catEntries.length === 1 ? 'y' : 'ies'}
              </p>
            </div>
            {canManage && (
              <Button
                size="sm" className="bg-[#0D2850] hover:bg-[#0B2246] gap-1 mr-6"
                onClick={() => onUpload?.(empGroup.employee_id)}
                data-testid="dialog-upload-btn"
              >
                <Upload className="w-3.5 h-3.5" />Upload
              </Button>
            )}
          </div>
        </DialogHeader>
        <div className="overflow-y-auto flex-1 -mx-6 px-6 pb-2">
          {docCount === 0 ? (
            <div className="text-center py-12">
              <FolderOpen className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p className="text-sm text-slate-400">No documents in this folder yet</p>
              {canManage && (
                <Button
                  variant="outline" size="sm" className="mt-3 gap-1"
                  onClick={() => onUpload?.(empGroup.employee_id)}
                >
                  <Upload className="w-3 h-3" />Upload first document
                </Button>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              {catEntries.map(([cat, docs]) => (
                <div key={cat}>
                  <div className="flex items-center gap-2 mb-2 sticky top-0 bg-white z-10 py-1">
                    <FolderOpen className="w-3.5 h-3.5 text-amber-500" />
                    <span className="text-xs font-semibold text-slate-600 uppercase tracking-wide">{cat}</span>
                    <Badge className="bg-slate-100 text-slate-500 hover:bg-slate-100 text-[10px]">{docs.length}</Badge>
                  </div>
                  <div className="space-y-2">
                    {docs.map(doc => (
                      <DocRow
                        key={doc.id}
                        doc={doc}
                        canManage={canManage}
                        canDelete={canDeleteAll || (doc.doc_type === 'personal' && doc.employee_id === getUser()?.employee_id)}
                        onReject={onReject}
                        onUndoReject={onUndoReject}
                        onDelete={onDelete}
                        onRefresh={onRefresh}
                      />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ─── Reject Dialog ────────────────────────────────────────
function RejectDialog({ doc, open, onOpenChange, onConfirm }) {
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);
  useEffect(() => { if (open) setReason(''); }, [open]);
  const submit = async () => {
    if (!reason.trim()) { toast.error('Please enter a rejection reason'); return; }
    setLoading(true);
    await onConfirm(doc, reason.trim());
    setLoading(false);
    onOpenChange(false);
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="reject-doc-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Ban className="w-4 h-4 text-amber-600" />Reject Document
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          {doc && (
            <div className="bg-slate-50 rounded-md p-3 text-xs">
              <p className="font-medium text-slate-700 truncate">{doc.filename}</p>
              <p className="text-slate-400">
                {doc.employee_name ? `${doc.employee_name} · ` : ''}{doc.category}
              </p>
            </div>
          )}
          <div>
            <Label className="text-xs">Rejection reason <span className="text-red-500">*</span></Label>
            <Textarea
              value={reason} onChange={e => setReason(e.target.value)}
              placeholder="e.g. Document is outdated, please upload latest version"
              rows={3}
              data-testid="reject-reason-input"
            />
            <p className="text-[10px] text-slate-400 mt-1">
              The document will stay visible with a red banner. Employee can re-upload a fresh copy.
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" className="flex-1" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button
              className="flex-1 bg-amber-600 hover:bg-amber-700"
              onClick={submit} disabled={loading}
              data-testid="confirm-reject-btn"
            >
              {loading ? 'Rejecting...' : 'Reject'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ─── Main Page ────────────────────────────────────────────
export default function DocumentsPage() {
  const manage = canManageDocs();
  const user = getUser();

  const [allDocs, setAllDocs] = useState([]); // employee view: company docs
  const [myDocs, setMyDocs] = useState([]); // employee view: personal docs
  const [categories, setCategories] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [catFilter, setCatFilter] = useState('All');

  const [openFolder, setOpenFolder] = useState(null); // empGroup currently open in dialog

  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadForm, setUploadForm] = useState({ employee_id: '', category: '', notes: '' });
  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const [myUploadOpen, setMyUploadOpen] = useState(false);
  const [myUploadForm, setMyUploadForm] = useState({ category: '', notes: '' });
  const [myUploadFile, setMyUploadFile] = useState(null);
  const myFileRef = useRef(null);

  const [addCatOpen, setAddCatOpen] = useState(false);
  const [newCatName, setNewCatName] = useState('');

  const [rejectDialog, setRejectDialog] = useState({ open: false, doc: null });

  // Load
  const load = async () => {
    setLoading(true);
    try {
      const promises = [
        api.get('/api/documents'),
        api.get('/api/documents/categories'),
      ];
      if (manage) promises.push(api.get('/api/employees?limit=200'));
      const results = await Promise.all(promises);
      const docs = results[0].data.documents || [];
      setCategories(results[1].data || []);
      if (manage) {
        setEmployees(results[2].data.employees || []);
        setAllDocs(docs);
      } else {
        // Employee: split into personal vs company
        const personal = [];
        const company = [];
        docs.forEach(empGroup => {
          const personalCats = {};
          const companyCats = {};
          Object.entries(empGroup.categories || {}).forEach(([cat, arr]) => {
            arr.forEach(d => {
              if (d.doc_type === 'personal') {
                if (!personalCats[cat]) personalCats[cat] = [];
                personalCats[cat].push(d);
              } else {
                if (!companyCats[cat]) companyCats[cat] = [];
                companyCats[cat].push(d);
              }
            });
          });
          if (Object.keys(personalCats).length) personal.push({ ...empGroup, categories: personalCats });
          if (Object.keys(companyCats).length) company.push({ ...empGroup, categories: companyCats });
        });
        setMyDocs(personal);
        setAllDocs(company);
      }
    } catch (e) { console.error(e); }
    setLoading(false);
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  // Refresh the open folder view when data reloads
  useEffect(() => {
    if (!openFolder) return;
    const source = manage ? allDocs : (openFolder._source === 'my' ? myDocs : allDocs);
    const updated = source.find(e => e.employee_id === openFolder.employee_id);
    if (updated) setOpenFolder({ ...updated, _source: openFolder._source });
    else setOpenFolder(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allDocs, myDocs]);

  // Filters
  const filteredFolders = useMemo(() => {
    return allDocs.filter(emp => {
      const matchSearch = !search
        || emp.employee_name?.toLowerCase().includes(search.toLowerCase())
        || emp.employee_code?.toLowerCase().includes(search.toLowerCase());
      if (catFilter !== 'All') return matchSearch && emp.categories?.[catFilter];
      return matchSearch;
    });
  }, [allDocs, search, catFilter]);

  const totalDocs = allDocs.reduce((sum, e) => sum + Object.values(e.categories || {}).reduce((s, d) => s + d.length, 0), 0);
  const totalMyDocs = myDocs.reduce((sum, e) => sum + Object.values(e.categories || {}).reduce((s, d) => s + d.length, 0), 0);

  // Upload handlers
  const openUploadForEmployee = (empId) => {
    setUploadForm({ employee_id: empId || '', category: '', notes: '' });
    setUploadFile(null);
    setUploadOpen(true);
  };

  const handleUpload = async () => {
    if (!uploadFile) { toast.error('Select a file'); return; }
    if (!uploadForm.employee_id || !uploadForm.category) {
      toast.error('Select employee and category'); return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', uploadFile);
      formData.append('employee_id', uploadForm.employee_id);
      formData.append('category', uploadForm.category);
      formData.append('notes', uploadForm.notes || '');
      await api.post('/api/documents/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Document uploaded');
      setUploadOpen(false);
      setUploadFile(null);
      setUploadForm({ employee_id: '', category: '', notes: '' });
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Upload failed'); }
    setUploading(false);
  };

  const handleMyUpload = async () => {
    if (!myUploadFile) { toast.error('Select a file'); return; }
    if (!myUploadForm.category) { toast.error('Select a category'); return; }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', myUploadFile);
      formData.append('category', myUploadForm.category);
      formData.append('notes', myUploadForm.notes || '');
      await api.post('/api/documents/my-upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Document uploaded successfully');
      setMyUploadOpen(false);
      setMyUploadFile(null);
      setMyUploadForm({ category: '', notes: '' });
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Upload failed'); }
    setUploading(false);
  };

  // Doc actions
  const handleDelete = async (docId) => {
    if (!window.confirm('Delete this document permanently? This cannot be undone.')) return;
    try {
      await api.delete(`/api/documents/${docId}`);
      toast.success('Deleted');
      await load();
    } catch (e) { toast.error('Failed to delete'); }
  };

  const handleReject = async (doc, reason) => {
    try {
      await api.post(`/api/documents/${doc.id}/reject`, { reason });
      toast.success('Document rejected');
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Reject failed'); }
  };

  const handleUndoReject = async (doc) => {
    try {
      await api.post(`/api/documents/${doc.id}/unreject`);
      toast.success('Document restored');
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Restore failed'); }
  };

  const handleAddCategory = async () => {
    if (!newCatName.trim()) return;
    try {
      await api.post('/api/documents/categories', { name: newCatName.trim() });
      toast.success(`Category '${newCatName.trim()}' added`);
      setNewCatName('');
      setAddCatOpen(false);
      const res = await api.get('/api/documents/categories');
      setCategories(res.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  // ─── Render ─────────────────────────────────────────────
  const isEmployee = !manage;
  const uploadBtn = isEmployee ? (
    <Button
      className="bg-[#0D2850] hover:bg-[#0B2246] gap-1"
      onClick={() => { setMyUploadFile(null); setMyUploadForm({ category: '', notes: '' }); setMyUploadOpen(true); }}
      data-testid="my-upload-btn"
    >
      <Upload className="w-4 h-4" />Upload Document
    </Button>
  ) : (
    <div className="flex gap-2">
      <Button variant="outline" size="sm" onClick={() => setAddCatOpen(true)} className="gap-1" data-testid="add-category-btn">
        <Plus className="w-3.5 h-3.5" />Category
      </Button>
      <Button className="bg-[#0D2850] hover:bg-[#0B2246] gap-1" onClick={() => openUploadForEmployee('')} data-testid="upload-doc-btn">
        <Upload className="w-4 h-4" />Upload Document
      </Button>
    </div>
  );

  return (
    <div className="space-y-6" data-testid="documents-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <Badge className="bg-[#0D2850] text-white text-[10px] mb-2 hover:bg-[#0D2850]">// DOCUMENTS</Badge>
          <h1 className="text-2xl font-semibold text-[#0B1B33] tracking-tight">
            {manage ? 'Employee Documents' : 'My Documents'}
          </h1>
          <p className="text-sm text-slate-500">
            {manage
              ? 'Browse each employee as a folder. Click to view, download, or reject uploaded documents.'
              : 'Your personal and company-assigned documents, grouped by folder.'}
          </p>
        </div>
        {uploadBtn}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        {manage ? (
          <>
            <StatCard icon={User} color="blue" label="Employees" value={allDocs.length} />
            <StatCard icon={FileText} color="emerald" label="Total Documents" value={totalDocs} />
            <StatCard icon={FolderOpen} color="violet" label="Categories" value={categories.length} />
          </>
        ) : (
          <>
            <StatCard icon={User} color="emerald" label="My Personal Docs" value={totalMyDocs} />
            <StatCard icon={Building2} color="blue" label="Company Documents" value={totalDocs} />
            <StatCard icon={FolderOpen} color="violet" label="Categories" value={categories.length} />
          </>
        )}
      </div>

      {/* Filters - Admin/HR only */}
      {manage && (
        <div className="flex gap-2 flex-wrap">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Search employee by name or code..."
              value={search} onChange={e => setSearch(e.target.value)}
              className="pl-9 h-9" data-testid="doc-search"
            />
          </div>
          <Select value={catFilter} onValueChange={setCatFilter}>
            <SelectTrigger className="w-[200px] h-9" data-testid="doc-cat-filter">
              <SelectValue placeholder="Category" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="All">All Categories</SelectItem>
              {categories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      )}

      {/* Folder Grid */}
      {loading ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-36 rounded-xl" />)}
        </div>
      ) : isEmployee ? (
        <EmployeeFoldersView
          myDocs={myDocs}
          companyDocs={allDocs}
          onOpen={setOpenFolder}
          onUpload={() => { setMyUploadFile(null); setMyUploadForm({ category: '', notes: '' }); setMyUploadOpen(true); }}
        />
      ) : filteredFolders.length === 0 ? (
        <Card className="border-slate-200">
          <CardContent className="p-12 text-center">
            <FolderOpen className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <p className="text-sm text-slate-400">
              {allDocs.length === 0 ? 'No documents uploaded yet' : 'No matching folders'}
            </p>
            <Button variant="outline" size="sm" className="mt-3 gap-1" onClick={() => openUploadForEmployee('')}>
              <Upload className="w-3 h-3" />Upload First Document
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="folder-grid">
          {filteredFolders.map(emp => (
            <FolderCard
              key={emp.employee_id}
              empGroup={emp}
              onOpen={(g) => setOpenFolder({ ...g, _source: 'admin' })}
              canManage={manage}
              onUpload={openUploadForEmployee}
            />
          ))}
        </div>
      )}

      {/* Folder Dialog */}
      <EmployeeDocsDialog
        empGroup={openFolder}
        open={!!openFolder}
        onOpenChange={(v) => { if (!v) setOpenFolder(null); }}
        canManage={manage}
        canDeleteAll={manage}
        onRefresh={load}
        onUpload={manage ? openUploadForEmployee : undefined}
        onReject={(doc) => setRejectDialog({ open: true, doc })}
        onUndoReject={handleUndoReject}
        onDelete={handleDelete}
      />

      {/* Reject Dialog */}
      <RejectDialog
        doc={rejectDialog.doc}
        open={rejectDialog.open}
        onOpenChange={(v) => setRejectDialog(s => ({ ...s, open: v }))}
        onConfirm={handleReject}
      />

      {/* Admin Upload Dialog */}
      <Dialog open={uploadOpen} onOpenChange={setUploadOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>Upload Document</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-xs">Employee</Label>
              <Select value={uploadForm.employee_id} onValueChange={v => setUploadForm({ ...uploadForm, employee_id: v })}>
                <SelectTrigger data-testid="upload-employee-select"><SelectValue placeholder="Select employee" /></SelectTrigger>
                <SelectContent>
                  {employees.map(e => <SelectItem key={e.id} value={e.id}>{e.name} ({e.employee_code})</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Category</Label>
              <Select value={uploadForm.category} onValueChange={v => setUploadForm({ ...uploadForm, category: v })}>
                <SelectTrigger data-testid="upload-category-select"><SelectValue placeholder="Select category" /></SelectTrigger>
                <SelectContent>
                  {categories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <FileDropZone
              file={uploadFile} setFile={setUploadFile} inputRef={fileRef}
              testId="doc-file-input"
            />
            <div>
              <Label className="text-xs">Notes (optional)</Label>
              <Input
                value={uploadForm.notes}
                onChange={e => setUploadForm({ ...uploadForm, notes: e.target.value })}
                placeholder="e.g. Front side, Updated copy..."
                data-testid="upload-notes"
              />
            </div>
            <Button className="w-full bg-[#0D2850] hover:bg-[#0B2246]" onClick={handleUpload} disabled={uploading} data-testid="submit-upload">
              {uploading ? 'Uploading...' : 'Upload Document'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Employee Upload Dialog */}
      <Dialog open={myUploadOpen} onOpenChange={setMyUploadOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>Upload Personal Document</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-xs">Category</Label>
              <Select value={myUploadForm.category} onValueChange={v => setMyUploadForm({ ...myUploadForm, category: v })}>
                <SelectTrigger data-testid="my-upload-category-select"><SelectValue placeholder="Select category" /></SelectTrigger>
                <SelectContent>
                  {PERSONAL_CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <FileDropZone
              file={myUploadFile} setFile={setMyUploadFile} inputRef={myFileRef}
              testId="my-doc-file-input"
            />
            <div>
              <Label className="text-xs">Notes (optional)</Label>
              <Input
                value={myUploadForm.notes}
                onChange={e => setMyUploadForm({ ...myUploadForm, notes: e.target.value })}
                placeholder="e.g. Updated copy, Front side..."
                data-testid="my-upload-notes"
              />
            </div>
            <Button className="w-full bg-[#0D2850] hover:bg-[#0B2246]" onClick={handleMyUpload} disabled={uploading} data-testid="submit-my-upload">
              {uploading ? 'Uploading...' : 'Upload Document'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Category Dialog */}
      <Dialog open={addCatOpen} onOpenChange={setAddCatOpen}>
        <DialogContent className="sm:max-w-xs">
          <DialogHeader><DialogTitle>Add Document Category</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Input value={newCatName} onChange={e => setNewCatName(e.target.value)} placeholder="e.g. Medical Records" data-testid="new-cat-input" />
            <Button className="w-full bg-[#0D2850] hover:bg-[#0B2246]" onClick={handleAddCategory} data-testid="submit-add-cat">Add Category</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ─── Helpers ──────────────────────────────────────────────
function StatCard({ icon: Icon, color, label, value }) {
  const colors = {
    blue: 'bg-blue-50 text-[#0D2850]',
    emerald: 'bg-emerald-50 text-emerald-600',
    violet: 'bg-violet-50 text-violet-600',
  };
  return (
    <Card className="border-slate-200">
      <CardContent className="p-3 flex items-center gap-3">
        <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${colors[color]}`}>
          <Icon className="w-4 h-4" />
        </div>
        <div>
          <p className="text-xs text-slate-500">{label}</p>
          <p className="text-xl font-semibold">{value}</p>
        </div>
      </CardContent>
    </Card>
  );
}

function FileDropZone({ file, setFile, inputRef, testId }) {
  return (
    <div>
      <Label className="text-xs">File</Label>
      <div
        className="border-2 border-dashed border-slate-200 rounded-lg p-4 text-center cursor-pointer hover:border-[#0D2850]/30 transition-colors"
        onClick={() => inputRef.current?.click()}
      >
        {file ? (
          <div>
            <File className="w-6 h-6 text-[#0D2850] mx-auto mb-1" />
            <p className="text-sm font-medium text-slate-700 truncate">{file.name}</p>
            <p className="text-xs text-slate-400">{formatSize(file.size)}</p>
          </div>
        ) : (
          <div>
            <Upload className="w-6 h-6 text-slate-300 mx-auto mb-1" />
            <p className="text-xs text-slate-400">Click to select file (max 10MB)</p>
          </div>
        )}
      </div>
      <input ref={inputRef} type="file" className="hidden" onChange={e => setFile(e.target.files?.[0] || null)} data-testid={testId} />
    </div>
  );
}

function EmployeeFoldersView({ myDocs, companyDocs, onOpen, onUpload }) {
  const user = getUser();
  const myFolder = myDocs[0] || {
    employee_id: user?.employee_id || '',
    employee_name: `${user?.name || 'Me'} (Personal)`,
    employee_code: user?.employee_code || '',
    categories: {},
  };
  const folders = [];
  folders.push({ ...myFolder, _source: 'my', _label: 'Personal', employee_name: `${user?.name || 'Me'} (Personal)` });
  companyDocs.forEach(c => folders.push({ ...c, _source: 'company', _label: 'Company' }));

  if (folders.length === 0 || (folders.length === 1 && Object.keys(folders[0].categories).length === 0)) {
    return (
      <Card className="border-slate-200 border-dashed">
        <CardContent className="p-10 text-center">
          <Upload className="w-10 h-10 text-slate-300 mx-auto mb-3" />
          <p className="text-sm font-medium text-slate-500">No documents yet</p>
          <p className="text-xs text-slate-400 mt-1 max-w-xs mx-auto">
            Upload your ID proofs, certificates, or any personal files for safekeeping
          </p>
          <Button variant="outline" size="sm" className="mt-4 gap-1" onClick={onUpload} data-testid="my-upload-empty-btn">
            <Upload className="w-3 h-3" />Upload Your First Document
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="folder-grid">
      {folders.map((f, idx) => {
        const docCount = Object.values(f.categories || {}).reduce((s, arr) => s + arr.length, 0);
        const catCount = Object.keys(f.categories || {}).length;
        return (
          <button
            key={`${f.employee_id}_${idx}_${f._source}`}
            type="button"
            onClick={() => onOpen(f)}
            className="group relative text-left rounded-xl border border-slate-200 bg-white hover:border-[#0D2850]/40 hover:shadow-md hover:-translate-y-0.5 transition-all p-4 flex flex-col gap-3"
            data-testid={`folder-${f._source}`}
          >
            <div className="w-11 h-11 rounded-lg bg-gradient-to-br from-amber-100 to-amber-200 flex items-center justify-center">
              <FolderOpen className="w-6 h-6 text-amber-600" />
            </div>
            <div>
              <p className="text-sm font-semibold text-[#0B1B33] truncate">{f.employee_name}</p>
              <p className="text-xs text-slate-400">{f._label}</p>
            </div>
            <div className="flex items-center gap-2">
              <Badge className="bg-slate-100 text-slate-600 hover:bg-slate-100 text-[10px]">{docCount} docs</Badge>
              {catCount > 0 && (
                <Badge className="bg-blue-50 text-blue-700 hover:bg-blue-50 text-[10px]">{catCount} {catCount === 1 ? 'category' : 'categories'}</Badge>
              )}
            </div>
          </button>
        );
      })}
    </div>
  );
}
